import React, { useRef, useState, useMemo, useEffect } from "react";
import { Box, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { AgGridTable } from "components/tables/AgGridTable";
import SearchIcon from "common/customIcons/search";
import { CloseRounded } from "@mui/icons-material";

const ProductTableView = ({ CarouselId, refreshKey, productLimit, products = [] }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const mainGridApiRef = useRef(null);
  const pageRef = useRef(0);
  const searchTermRef = useRef("");

  // Process products data
  const processedProducts = useMemo(() => {
    if (!Array.isArray(products)) {
      return [];
    }

    return products.map((product) => ({
      ...product,
      id: product?.productId || product?.id || product?._id,
      productId: product?.productId || product?.id || product?._id,
      productName: product?.productName || '-',
      brandName: product?.brandName || '-',
      strainType: product?.strainType || '-',
      totalQuantity: product?.totalQuantity || 0,
      productPrice: product?.productPrice || product?.price || 0,
      productTraits: product?.productTraits || [],
    }));
  }, [products]);

  // Column definitions
  const columnDefs = useMemo(() => [
    {
      headerName: "Product Name",
      field: "productName",
      sortable: true,
      filter: false,
      flex: 1,
      minWidth: 180,
      menuTabs: [], // Remove column menu
      comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
        // Case-insensitive string comparison
        const valA = String(valueA || '').toLowerCase();
        const valB = String(valueB || '').toLowerCase();
        return valA.localeCompare(valB);
      }
    },
    {
      headerName: "Strain Type",
      field: "strainType",
      sortable: true,
      filter: false,
      flex: 1,
      minWidth: 130,
      menuTabs: [], // Remove column menu
      comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
        const valA = String(valueA || '').toLowerCase();
        const valB = String(valueB || '').toLowerCase();
        return valA.localeCompare(valB);
      }
    },
    {
      headerName: "Product Brand",
      field: "brandName",
      sortable: true,
      filter: false,
      flex: 1,
      minWidth: 150,
      menuTabs: [], // Remove column menu
      comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
        const valA = String(valueA || '').toLowerCase();
        const valB = String(valueB || '').toLowerCase();
        return valA.localeCompare(valB);
      }
    },
    {
      headerName: "Inventory",
      field: "totalQuantity",
      sortable: true,
      filter: false,
      flex: 1,
      minWidth: 80,
      minWidth: 120,
      menuTabs: [], // Remove column menu
      comparator: (valueA, valueB) => {
        // Numeric comparison for inventory
        return (valueA || 0) - (valueB || 0);
      }
    },
    {
      headerName: "Price",
      field: "totalPrice",
      sortable: true,
      filter: false,
      flex: 1,
      minWidth: 80,
      valueGetter: (params) => {
        // Return totalPrice if exists, otherwise productPrice
        return params.data?.totalPrice ?? params.data?.productPrice;
      },
      valueFormatter: (params) => {
        const value = params.value;
        return value ? `$${value}` : '-';
      },
      // valueFormatter: (params) => params.value ? `$${params.value}` : '-',
      menuTabs: [], // Remove column menu
      comparator: (valueA, valueB) => {
        // Numeric comparison for price
        return (parseFloat(valueA) || 0) - (parseFloat(valueB) || 0);
      }
    },
    {
      headerName: "Traits",
      field: "productTraits",
      sortable: true,
      filter: false,
      flex: 1.5,
      minWidth: 200,
      menuTabs: [], // Remove column menu
      comparator: (valueA, valueB) => {
        // Sort by the first trait name if available
        const traitsA = Array.isArray(valueA) ? valueA.map(t => t?.subTraitName?.toLowerCase() || '').filter(Boolean) : [];
        const traitsB = Array.isArray(valueB) ? valueB.map(t => t?.subTraitName?.toLowerCase() || '').filter(Boolean) : [];
        const strA = traitsA.length > 0 ? traitsA[0] : '';
        const strB = traitsB.length > 0 ? traitsB[0] : '';
        return strA.localeCompare(strB);
      },
      cellRenderer: (params) => {
        const traits = params.value || [];
        if (!Array.isArray(traits) || traits.length === 0) return '-';
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {traits.map((trait, idx) => (
              <span
                key={idx}
                style={{
                  background: '#f0f0f0',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  display: 'inline-block'
                }}
              >
                {trait?.subTraitName || ''}
              </span>
            ))}
          </div>
        );
      },
    },
  ], []);

  const getRows = useMemo(() => {
    return async (params) => {
      try {
        let filtered = [...processedProducts];
        const term = searchTermRef.current.toLowerCase().trim();

        // Apply search filter
        if (term) {
          filtered = filtered.filter((p) => {
            const haystack = [
              p?.productName?.toLowerCase() || '',
              p?.brandName?.toLowerCase() || '',
              p?.strainType?.toLowerCase() || '',
              Array.isArray(p?.productTraits)
                ? p.productTraits.map((t) => t?.subTraitName?.toLowerCase() || '').filter(Boolean).join(" ")
                : "",
            ].filter(Boolean).join(" ");

            return haystack.includes(term);
          });
        }

        // Apply sorting
        if (params.sortModel?.length > 0) {
          const sortModel = params.sortModel[0];
          filtered.sort((a, b) => {
            const aValue = a[sortModel.colId];
            const bValue = b[sortModel.colId];

            // Handle numeric values
            if (sortModel.colId === 'productPrice' || sortModel.colId === 'totalQuantity') {
              const numA = parseFloat(aValue) || 0;
              const numB = parseFloat(bValue) || 0;
              return sortModel.sort === 'asc' ? numA - numB : numB - numA;
            }

            // Handle traits array
            if (sortModel.colId === 'productTraits') {
              const traitsA = Array.isArray(aValue) ? aValue.map(t => t?.subTraitName?.toLowerCase() || '').filter(Boolean) : [];
              const traitsB = Array.isArray(bValue) ? bValue.map(t => t?.subTraitName?.toLowerCase() || '').filter(Boolean) : [];
              const strA = traitsA.length > 0 ? traitsA[0] : '';
              const strB = traitsB.length > 0 ? traitsB[0] : '';
              const compareResult = strA.localeCompare(strB);
              return sortModel.sort === 'asc' ? compareResult : -compareResult;
            }

            // Handle string values
            const strA = String(aValue || '').toLowerCase();
            const strB = String(bValue || '').toLowerCase();
            const compareResult = strA.localeCompare(strB);
            return sortModel.sort === 'asc' ? compareResult : -compareResult;
          });
        }

        // Return the results
        params.successCallback(filtered, filtered.length);
      } catch (error) {
        params.failCallback();
      }
    };
  }, [processedProducts]);

  const searchHandler = (searchValue) => {
    searchTermRef.current = searchValue;
    setSearchTerm(searchValue);
    if (mainGridApiRef.current) {
      mainGridApiRef.current.setGridOption('datasource', { getRows });
    }
  };
  useEffect(() => {
    if (mainGridApiRef.current) {
      const dataSource = {
        getRows: getRows
      };
      mainGridApiRef.current.setGridOption('datasource', dataSource);
    }
  }, [processedProducts, getRows]);
  return (
    <Box mt={2}>
      <Stack direction="row" justifyContent="space-between" flexWrap="wrap" alignItems="center" gap={2} mb={2}>
        <Typography variant="h6" fontWeight="semibold">Selected Products</Typography>
        <TextField
          className="custom-search-field remove-white"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => searchHandler(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton size="small">
                  <SearchIcon width="18px" height="18px" />
                </IconButton>
              </InputAdornment>
            ),
            endAdornment: searchTerm?.length > 0 ? (
              <InputAdornment>
                <IconButton onClick={() => searchHandler("")} size="small">
                  <CloseRounded />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          style={{ minWidth: "300px" }}
        />
      </Stack>

      <div className="last-child-left">
        <AgGridTable
          col={columnDefs}
          getRows={getRows}
          mainGridApiRef={mainGridApiRef}
          pageRef={pageRef}
          gridOptions={{
            headerHeight: 38,
            rowHeight: 64,
            defaultColDef: {
              sortable: true,
              resizable: true,
              filter: false, // Disable filtering
              suppressMenu: true, // Hide column menu
              menuTabs: [], // Remove all menu tabs
            },
            suppressMenuHide: true, // Completely remove the menu
            animateRows: true,
          }}
          style={{ height: "60vh" }}
        />
      </div>
    </Box>
  );
};

export default ProductTableView;